import { useTRPC } from "@nwga/features/client";
import { JobForm } from "@nwga/features/jobs/ui";
import { Button } from "@nwga/ui/components/button";
import { Skeleton } from "@nwga/ui/components/skeleton";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { PageHeader } from "../../../../components/page-header.tsx";

/**
 * Composing a listing.
 *
 * Its own route rather than a panel that expands inside the list: a job
 * description is the longest piece of writing anyone does in this product, and
 * it deserves a URL that survives a refresh, can be opened in a second tab, and
 * does not sit inside a list that re-renders underneath it.
 *
 * Why the business's state is not restated here: the workspace layout already
 * carries the banner explaining that publishing is locked until approval, on
 * every screen including this one.
 */
export const Route = createFileRoute("/dashboard/$businessSlug/jobs/new")({
	component: NewJobPage,
	loader: ({ context, params }) =>
		context.queryClient.ensureQueryData(
			context.trpc.businesses.bySlug.queryOptions({
				slug: params.businessSlug,
			}),
		),
});

function NewJobPage() {
	const { businessSlug } = Route.useParams();
	const trpc = useTRPC();
	const navigate = useNavigate();

	const business = useQuery(
		trpc.businesses.bySlug.queryOptions({ slug: businessSlug }),
	);

	if (!business.isSuccess) return <Skeleton className="h-96 w-full" />;

	return (
		<div className="max-w-2xl space-y-6">
			<Button asChild variant="ghost" size="sm" className="-ml-2">
				<Link to="/dashboard/$businessSlug/jobs" params={{ businessSlug }}>
					<ArrowLeftIcon className="size-4" aria-hidden="true" />
					Job listings
				</Link>
			</Button>

			<PageHeader
				title="New listing"
				description="Describe the role the way you would to somebody who walked in and asked about it."
			/>

			<JobForm
				businessId={business.data.id}
				canPublish={business.data.status === "active"}
				onSaved={() =>
					void navigate({
						to: "/dashboard/$businessSlug/jobs",
						params: { businessSlug },
					})
				}
			/>
		</div>
	);
}
